import React, { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import '../../CSS/Ambassade1.css';
import { FaTimes } from "react-icons/fa";
import { useLanguageData } from '../../hooks/useLanguageData';
import SafeHTML from "../SafeHTML";

const Ambassade = () => {
    const { t, i18n } = useTranslation();
    const [showAmbassadorsModal, setShowAmbassadorsModal] = useState(false);
    const [anciensAmbassadeurs, setAnciensAmbassadeurs] = useState([]);
    
    // Utiliser useLanguageData pour chaque appel API
    const { 
        data: beninChineResponse, 
        loading: beninChineLoading, 
        error: beninChineError 
    } = useLanguageData('/api/v1/embassy/static-data?slug=presentation_ambassy_china');

    const { 
        data: leadershipResponse, 
        loading: leadershipLoading, 
        error: leadershipError 
    } = useLanguageData('/api/v1/embassy/static-data?slug=diplomatie_inovation_multimaterialisme_chine');

    const { 
        data: whiteBlockResponse, 
        loading: whiteBlockLoading, 
        error: whiteBlockError 
    } = useLanguageData('/api/v1/embassy/static-data?slug=diplomatie_benin_chine');

    const { 
        data: ambassadorsResponse, 
        loading: ambassadorsLoading, 
        error: ambassadorsError,
        refetch: refetchAmbassadors 
    } = useLanguageData('/api/v1/embassy/old');

    // États pour les données transformées
    const [beninChineData, setBeninChineData] = useState(null);
    const [leadershipData, setLeadershipData] = useState(null);
    const [whiteBlockData, setWhiteBlockData] = useState(null);

    // ✅ Fonction pour formater la date selon la langue (comme dans le premier code)
    const formatDateByLanguage = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        
        // Vérifier si la date est valide
        if (isNaN(date.getTime())) return '';
        
        const currentLanguage = i18n.language;
        
        // Format français: jour mois année
        if (currentLanguage === 'fr') {
            return date.toLocaleDateString('fr-FR', {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
            });
        }
        
        // Format anglais: mois jour, année
        if (currentLanguage === 'en') {
            return date.toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric'
            });
        }
        
        // Format chinois: année mois jour
        if (currentLanguage === 'zh') {
            return date.toLocaleDateString('zh-CN', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
        }
        
        // Format par défaut: français
        return date.toLocaleDateString('fr-FR', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    // Fonction pour formater la date (alias pour compatibilité)
    const formatDate = (dateString) => {
        return formatDateByLanguage(dateString);
    };

    // Fonction pour convertir les civilités en abréviations
    const getCivilityAbbreviation = (civility) => {
        const civilityMap = {
            'Monsieur': 'M.',
            'Madame': 'Mme',
            'Maître': 'Me',
            'Docteur': 'Dr',
            'Camarade': 'Cde',
            'Chef de Bataillon': 'Ch. Btn',
            'Chef de Bataillon/Lieutenant-Colonel': 'Ch. Btn/Lt-Col',
            'Lieutenant-Colonel': 'Lt-Col'
        };

        return civilityMap[civility] || civility;
    };

    // Transformer les données Benin Chine
    useEffect(() => {
        if (beninChineResponse?.data && beninChineResponse.data.length > 0) {
            console.log('📊 Données Benin Chine reçues:', beninChineResponse);
            setBeninChineData(beninChineResponse.data[0]);
        }
    }, [beninChineResponse]);

    // Transformer les données leadership
    useEffect(() => {
        if (leadershipResponse?.data && leadershipResponse.data.length > 0) {
            console.log('📊 Données leadership reçues:', leadershipResponse);
            setLeadershipData(leadershipResponse.data[0]);
        }
    }, [leadershipResponse]);

    // Transformer les données bloc blanc
    useEffect(() => {
        if (whiteBlockResponse?.data && whiteBlockResponse.data.length > 0) {
            console.log('📊 Données bloc blanc reçues:', whiteBlockResponse);
            setWhiteBlockData(whiteBlockResponse.data[0]);
        }
    }, [whiteBlockResponse]);

    // ✅ Transformer les données des anciens ambassadeurs (avec tri et traduction)
    useEffect(() => {
        if (ambassadorsResponse?.data && Array.isArray(ambassadorsResponse.data)) {
            console.log('👥 Données anciens ambassadeurs reçues:', ambassadorsResponse);
            
            // ✅ Trier les ambassadeurs par date de début (du plus récent au plus ancien)
            const sortedAmbassadors = [...ambassadorsResponse.data].sort((a, b) => {
                const dateA = new Date(a.dateStartAppointment?.date);
                const dateB = new Date(b.dateStartAppointment?.date);
                return dateB - dateA; // Tri décroissant (récent → ancien)
            });

            const ambassadeursFormattes = sortedAmbassadors.map((ambassadeur, index) => {
                const civilityAbbrev = getCivilityAbbreviation(ambassadeur.civility);
                
                const startDate = formatDateByLanguage(ambassadeur.dateStartAppointment?.date);
                const endDate = formatDateByLanguage(ambassadeur.dateEndAppointment?.date);

                return {
                    id: ambassadeur.id || `amb-${index}`,
                    civility: ambassadeur.civility,
                    civilityAbbrev: civilityAbbrev,
                    lastname: ambassadeur.lastname,
                    firstname: ambassadeur.firstname,
                    picture: ambassadeur.picture,
                    dateStart: ambassadeur.dateStartAppointment?.date,
                    dateEnd: ambassadeur.dateEndAppointment?.date,
                    dates: startDate && endDate ? [`${startDate} - ${endDate}`] : [],
                    name: `${civilityAbbrev} ${ambassadeur.firstname} ${ambassadeur.lastname}`,
                    description: `Ambassadeur du ${startDate} au ${endDate}`
                };
            });

            setAnciensAmbassadeurs(ambassadeursFormattes);
        }
    }, [ambassadorsResponse, i18n.language]);

    const handleAmbassadorsClick = async (e) => {
        e.preventDefault();
        if (anciensAmbassadeurs.length === 0) {
            await refetchAmbassadors();
        }
        setShowAmbassadorsModal(true);
    };

    const handleCloseModal = () => {
        setShowAmbassadorsModal(false);
    };

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) {
            handleCloseModal();
        }
    };

    // Composant de spinner de chargement
    const LoadingSpinner = () => (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: '200px'
        }}>
            <div style={{
                width: "50px",
                height: "50px",
                border: "4px solid #f3f3f3",
                borderTop: "4px solid #1a3c5e",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
                marginBottom: "1rem"
            }}></div>

            <style>
                {`
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                `}
            </style>
        </div>
    );

    // Composant pour Information indisponible (traduit)
    const InfoIndisponible = () => (
        <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '200px',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            color: '#999',
            fontStyle: 'italic',
            fontSize: '1rem',
            textAlign: 'center',
            padding: '2rem'
        }}>
            {t('common.infoUnavailable')}
        </div>
    );

    return (
        <div className="ambassade-container">
            <div className="ambassade-content">
                {/* Premier bloc - Benin Chine */}
                {beninChineLoading ? (
                    <div className="ambassade-texte">
                        <div className="ambassade-paragraphe p-4">
                            <LoadingSpinner />
                        </div>
                        <div className="ambassade-img">
                            <LoadingSpinner />
                        </div>
                    </div>
                ) : beninChineError || !beninChineData ? (
                    <div className="ambassade-texte">
                        <div className="ambassade-paragraphe" style={{ width: '100%' }}>
                            <InfoIndisponible />
                        </div>
                    </div>
                ) : (
                    <div className="ambassade-texte">
                        <div className="ambassade-paragraphe" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                            <div id='ambasdata'>
                                <SafeHTML html={beninChineData.content} />
                            </div>
                        </div>
                        <div className="ambassade-img">
                            {beninChineData.image && (
                                <img
                                    src={beninChineData.image}
                                    alt={beninChineData.name || t('ambassade.beninChinaAlt')}
                                />
                            )}
                        </div>
                    </div>
                )}

                {/* SECTION 2 - Leadership et bloc blanc */}
                <section className="section-2" style={{ display: "flex", flexDirection: "row", gap: "20px" }}>
                    {/* PREMIER BLOC : Image + Texte bleu (Leadership) */}
                    {leadershipLoading ? (
                        <div className="leadership-combined-block">
                            <div className="presentation-leadership blue-bg">
                                <div className="presentation-leadership-paragraphe">
                                    <LoadingSpinner />
                                </div>
                            </div>
                            <div className="leadership-image-block">
                                <LoadingSpinner />
                            </div>
                        </div>
                    ) : leadershipError || !leadershipData ? (
                        <div className="leadership-combined-block">
                            <div className="presentation-leadership blue-bg" style={{ width: '100%' }}>
                                <div className="presentation-leadership-paragraphe">
                                    <InfoIndisponible />
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="leadership-combined-block">
                            <div className="presentation-leadership blue-bg">
                                <div className="presentation-leadership-paragraphe">
                                    <div id="content-leader" className="content-leader">
                                        <SafeHTML html={leadershipData.content} />
                                    </div>
                                    <a className="subtitle-modal" href='' style={{ color: "#ffffffff" }} onClick={handleAmbassadorsClick}>
                                        {t('ambassade.formerAmbassadors')}
                                    </a>
                                </div>
                            </div>
                            <div className="leadership-image-block mobile-hidden">
                                {leadershipData.image && (
                                    <img
                                        src={leadershipData.image}
                                        alt={leadershipData.name || t('ambassade.leadershipAlt')}
                                        className="leader-portrait"
                                    />
                                )}
                            </div>
                        </div>
                    )}

                    {/* DEUXIÈME BLOC : Texte blanc seul - RÉCUPÉRÉ DEPUIS L'API */}
                    {whiteBlockLoading ? (
                        <div className="presentation-leadership white-bg">
                            <div className="presentation-leadership-paragraphe">
                                <LoadingSpinner />
                            </div>
                        </div>
                    ) : whiteBlockError || !whiteBlockData ? (
                        <div className="presentation-leadership white-bg">
                            <div className="presentation-leadership-paragraphe">
                                <InfoIndisponible />
                            </div>
                        </div>
                    ) : (
                        <div className="presentation-leadership white-bg">
                            <div className="presentation-leadership-paragraphe">
                                <div id="content-leader" className="content-leader" style={{ justifyContent: 'left' }}>
                                    <SafeHTML html={whiteBlockData.content} />
                                </div>
                            </div>
                        </div>
                    )}
                </section>

                {/* Modal des Anciens Ambassadeurs */}
                {showAmbassadorsModal && (
                    <div className="ambassadors-modal-backdrop" onClick={handleBackdropClick}>
                        <div className="ambassadors-modal-content">
                            <div className="ambassadors-modal-header">
                                <h2 className="ambassadors-modal-title">{t('ambassade.formerAmbassadorsTitle')}</h2>
                                <button
                                    className="ambassadors-modal-close"
                                    onClick={handleCloseModal}
                                    aria-label={t('common.close')}
                                >
                                    <FaTimes />
                                </button>
                            </div>
                            <div className="ambassadors-modal-body">
                                {ambassadorsLoading ? (
                                    <LoadingSpinner />
                                ) : ambassadorsError || anciensAmbassadeurs.length === 0 ? (
                                    <div className="full-width-info-container">
                                        <InfoIndisponible />
                                    </div>
                                ) : (
                                    <div className="cooperation-grid">
                                        {anciensAmbassadeurs.map((ambassadeur) => (
                                            <div
                                                key={ambassadeur.id}
                                                className="cooperation-card"
                                            >
                                                {ambassadeur.picture ? (
                                                    <img
                                                        src={ambassadeur.picture}
                                                        alt={`${ambassadeur.firstname} ${ambassadeur.lastname}`}
                                                        className="ambassador-image"
                                                    />
                                                ) : (
                                                    <div className="image-placeholder-small">
                                                        <div className="placeholder-icon">👤</div>
                                                    </div>
                                                )}
                                                <div className="date-name-container">
                                                    <div className="date-container">
                                                        <div className="dates">
                                                            {ambassadeur.dates.map((date, index) => (
                                                                <div key={index} className="date-line">{date}</div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                    <h3>{ambassadeur.name}</h3>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Styles CSS supplémentaires */}
            <style>
                {`
                    .full-width-info-container {
                        display: flex;
                        justify-content: center;
                        align-items: center;
                        width: 100%;
                        min-height: 300px;
                        padding: 2rem;
                    }

                    .image-placeholder-small {
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        justify-content: center;
                       
                        background-color: #f8f9fa;
                        border: 2px dashed #dee2e6;
                        border-radius: 8px;
                        color: #6c757d;
                        text-align: center;
                        padding: 1rem;
                    }

                    .placeholder-icon {
                        font-size: 3rem;
                        margin-bottom: 1rem;
                        opacity: 0.5;
                    }
                `}
            </style>
        </div>
    );
};

export default Ambassade;